import { z } from "zod";

/**
 * The one vocabulary for where a code-interpreter turn's PRELUDE time goes —
 * everything the server does between taking the turn and the runner's first
 * content event. Both surfaces spell their spans from this object: agent chat
 * (in-app and public v1 `/chat`, the v1 inline runner) and Cue (the v2
 * bundled runner). A span a surface never reaches is simply absent from its
 * rows; there is no second naming for either of them.
 *
 * Names are dotted by phase so a reader can group without a lookup table:
 *
 *   - `orchestrate.*` a surface's own turn assembly before the provider call
 *   - `acquire.*`   finding or creating the sandbox and its session row
 *   - `create.*`    the Daytona create call, split by `onPhaseComplete`
 *   - `wake.*`      restarting a stopped sandbox that already has a row
 *   - `mount.*`     sandbox mounts — shared by both surfaces, one writer
 *   - `readiness.*` per-turn sandbox preparation before the runner can start
 *   - `launch.*`    building the runner's input and handing its launch line to
 *                   the sandbox — host-side only; the process's boot is on the
 *                   sandbox clock, in `RunnerBootTiming`
 *
 * `mount.exec` repeats once per workspace, carrying its position in `attrs`.
 * `launch.transport-*` is the session transport into the sandbox.
 */
export const SANDBOX_PRELUDE_SPAN = {
  orchestrateInitialQueries: "orchestrate.initial-queries",
  orchestratePersistTurnInput: "orchestrate.persist-turn-input",
  orchestratePrepareTurn: "orchestrate.prepare-turn",
  orchestrateHistoryBuild: "orchestrate.history-build",
  acquire: "acquire",
  acquireSessionLookup: "acquire.session-lookup",
  acquireSkillCount: "acquire.skill-count",
  acquireMountResolve: "acquire.mount-resolve",
  acquireSessionUpsert: "acquire.session-upsert",
  createApiCall: "create.api-call",
  createWaitStarted: "create.wait-started",
  wakeGet: "wake.get",
  wakeStart: "wake.start",
  wakeGetStarted: "wake.get-started",
  wakeRemount: "wake.remount",
  mountStsAssume: "mount.sts-assume",
  mountExec: "mount.exec",
  mountChannelWrite: "mount.channel-write",
  readiness: "readiness",
  readinessMkdir: "readiness.mkdir",
  readinessDeps: "readiness.deps",
  readinessAfterCreate: "readiness.after-create",
  readinessSkillsFingerprint: "readiness.skills-fingerprint",
  readinessSkillsDownload: "readiness.skills-download",
  readinessSkillsReconcile: "readiness.skills-reconcile",
  readinessSkillsChown: "readiness.skills-chown",
  readinessSkillsUpload: "readiness.skills-upload",
  launchSessionFileCheck: "launch.session-file-check",
  launchApiKeyRead: "launch.api-key-read",
  launchScriptGenerate: "launch.script-generate",
  launchPayloadUpload: "launch.payload-upload",
  launchTransportCreate: "launch.transport-create",
  launchTransportConnect: "launch.transport-connect",
  /**
   * The host writing the launch line onto the connected transport — one
   * websocket send, and nothing the sandbox does after receiving it. The shell,
   * its `exec`, the runner's process start, module load and SDK evaluation are
   * timed on the sandbox's own clock instead, in the `runnerBoot` group of the
   * same trace's `daytona_timing_marks` event (`RunnerBootTiming`).
   */
  launchRunner: "launch.runner"
} as const;

export type SandboxPreludeSpanName =
  (typeof SANDBOX_PRELUDE_SPAN)[keyof typeof SANDBOX_PRELUDE_SPAN];

export const SANDBOX_PRELUDE_SPAN_NAMES = Object.values(SANDBOX_PRELUDE_SPAN) as [
  SandboxPreludeSpanName,
  ...SandboxPreludeSpanName[]
];

/**
 * Metric discriminator on the `SDK_RAW` Daytona trace event that carries a
 * turn's prelude spans — one event per turn, holding every span, beside the
 * `cold_start` and `daytona_timing_marks` rows the same trace already carries.
 */
export const SANDBOX_PRELUDE_SPANS_METRIC = "sandbox_prelude_spans" as const;

/**
 * One timed step. `startOffsetMs` is measured from the payload's
 * `originEpochMs`, so a row is read on ONE clock — the app server's — and never
 * mixes it with the database's insert time. `ok` is false when the step threw;
 * the throw itself still propagated to the caller unchanged.
 */
export const SandboxPreludeSpanSchema = z.object({
  name: z.enum(SANDBOX_PRELUDE_SPAN_NAMES),
  startOffsetMs: z.number(),
  durationMs: z.number().nonnegative(),
  ok: z.boolean(),
  attrs: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional()
});

export type SandboxPreludeSpan = z.infer<typeof SandboxPreludeSpanSchema>;

/**
 * The trace-event payload. `originEpochMs` is the instant the surface took the
 * turn — the same instant a code-interpreter generation's `ttftMs` counts from
 * — so `ttftMs` minus the time the spans COVER is the time nothing here names.
 * Covered, never summed: spans nest (`acquire` encloses `acquire.*` and the
 * `create.*` phases), so a sum counts the same milliseconds more than once.
 *
 * `ttftMs` is that generation's own value, written from the code that fills it,
 * so a reader shows the stored figure rather than deriving a second one. `null`
 * means the turn reached no content. It is optional because events written
 * before the field existed carry none, and those must still parse.
 */
export const SandboxPreludeSpansPayloadSchema = z.object({
  metric: z.literal(SANDBOX_PRELUDE_SPANS_METRIC),
  originEpochMs: z.number(),
  spans: z.array(SandboxPreludeSpanSchema),
  ttftMs: z.number().nonnegative().nullable().optional()
});

export type SandboxPreludeSpansPayload = z.infer<typeof SandboxPreludeSpansPayloadSchema>;
